import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Wand2, CheckCircle, AlertTriangle, AlertCircle, TrendingUp } from 'lucide-react';
import { marked } from 'marked';

interface SeoAnalysisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'analyze' | 'optimize';
  data: {
    contentHealthScore?: number;
    seoScore?: number;
    recommendations?: string;
    improvements?: string;
    metaTitle?: string;
    metaDescription?: string;
    providerUsed?: string;
    modelUsed?: string;
  } | null;
}

const getScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBgColor = (score: number) => {
  if (score >= 80) return 'bg-green-100 border-green-200';
  if (score >= 60) return 'bg-yellow-100 border-yellow-200';
  return 'bg-red-100 border-red-200';
};

const getScoreIcon = (score: number) => {
  if (score >= 80) return <CheckCircle className="h-6 w-6 text-green-600" />;
  if (score >= 60) return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
  return <AlertCircle className="h-6 w-6 text-red-600" />;
};

export const SeoAnalysisDialog: React.FC<SeoAnalysisDialogProps> = ({
  open,
  onOpenChange,
  type,
  data,
}) => {
  if (!data) return null;

  const isAnalysis = type === 'analyze';
  const score = isAnalysis ? data.contentHealthScore : data.seoScore;
  const content = isAnalysis ? data.recommendations : data.improvements;

  // Parse markdown content to HTML
  const parsedContent = content ? marked.parse(content, { async: false }) as string : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col p-0 mr-12" style={{ zIndex: 60 }}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            {isAnalysis ? (
              <BarChart3 className="h-5 w-5 text-primary" />
            ) : (
              <Wand2 className="h-5 w-5 text-primary" />
            )}
            {isAnalysis ? 'SEO Analysis Results' : 'SEO Optimization Results'}
          </SheetTitle>
          <SheetDescription>
            {isAnalysis
              ? 'Analysis of your content for SEO improvements'
              : 'Suggestions to improve your content\'s SEO performance'}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-6">
            {/* Score Display */}
            {score !== undefined && score !== null && (
              <div className={`flex items-center gap-4 p-4 rounded-lg border ${getScoreBgColor(score)}`}>
                {getScoreIcon(score)}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {isAnalysis ? 'Content Health Score' : 'SEO Score'}
                  </p>
                  <p className={`text-3xl font-bold ${getScoreColor(score)}`}>
                    {score}/100
                  </p>
                </div>
                <div className="ml-auto">
                  <Badge variant={score >= 80 ? 'default' : score >= 60 ? 'secondary' : 'destructive'}>
                    {score >= 80 ? 'Excellent' : score >= 60 ? 'Needs Work' : 'Poor'}
                  </Badge>
                </div>
              </div>
            )}

            {/* Provider Info */}
            {data.modelUsed && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Powered by:</span>
                <Badge variant="outline" className="text-xs">{data.modelUsed}</Badge>
              </div>
            )}

            {/* Meta Suggestions (for optimize) */}
            {!isAnalysis && (data.metaTitle || data.metaDescription) && (
              <div className="space-y-3 p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TrendingUp className="h-4 w-4" />
                  Applied Improvements
                </div>
                {data.metaTitle && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Meta Title</p>
                    <p className="text-sm bg-background p-2 rounded border">{data.metaTitle}</p>
                  </div>
                )}
                {data.metaDescription && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Meta Description</p>
                    <p className="text-sm bg-background p-2 rounded border">{data.metaDescription}</p>
                  </div>
                )}
              </div>
            )}

            {/* Recommendations/Improvements Content */}
            {content && (
              <div className="rounded-lg border p-4">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  {isAnalysis ? (
                    <>
                      <BarChart3 className="h-4 w-4" />
                      Recommendations
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-4 w-4" />
                      Improvement Suggestions
                    </>
                  )}
                </h4>
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: parsedContent }}
                />
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
